import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { PhotoAlbumStateModel } from 'models/setting/photo-album.model';
import { ALBUM_RESOURCE_TYPES } from 'constants/booth.const';
import {
  activePhotoAlbumAction,
  createPhotoAlbumAction,
  deletePhotoAlbumAction,
  downloadResourcePhotoAlbumAction,
  getPhotoAlbumAction,
  getResourcePhotoAlbumAction,
  inactivePhotoAlbumAction,
  listPhotoAlbumAction,
  updatePhotoAlbumAction,
} from './photo-album.action';

const initialState: PhotoAlbumStateModel = {
  photoAlbumListData: null,
  photoAlbumDetail: null,
  triggerSavePhotoAlbum: null,

  loadingList: false,
  loadingDetail: false,
  loadingDownload: null,
};

const getStateKeyByType = (type: number) => {
  switch (type) {
    case ALBUM_RESOURCE_TYPES.BOOTH_IMAGES:
      return 'boothImages';
    case ALBUM_RESOURCE_TYPES.VIDEO:
      return 'boothVideos';
    case ALBUM_RESOURCE_TYPES.UPLOAD_IMAGES:
      return 'customerImages';
    default:
      return 'boothImages';
  }
};

export const photoAlbumSlice = createSlice({
  name: 'photoAlbum',
  initialState,
  reducers: {
    setPhotoAlbumStateAction: (
      state,
      action: PayloadAction<Partial<PhotoAlbumStateModel>>,
    ) =>
      ({
        ...state,
        ...action?.payload,
      } as PhotoAlbumStateModel),
    resetPhotoAlbumStateAction: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPhotoAlbumAction.pending, (state) => ({
        ...state,
        photoAlbumDetail: null,
        loadingDetail: true,
      }))
      .addCase(getPhotoAlbumAction.fulfilled, (state, { payload }) => ({
        ...state,
        photoAlbumDetail: payload?.data || null,
        loadingDetail: false,
      }))
      .addCase(getPhotoAlbumAction.rejected, (state) => ({
        ...state,
        photoAlbumDetail: null,
        loadingDetail: false,
      }))
      .addCase(listPhotoAlbumAction.pending, (state) => ({
        ...state,
        loadingList: true,
      }))
      .addCase(listPhotoAlbumAction.fulfilled, (state, { payload }) => ({
        ...state,
        loadingList: false,
        photoAlbumListData: {
          items: payload?.data?.items || [],
          totalItems: payload?.data?.totalItems || 0,
          totalPages: payload?.data?.totalPages || 0,
        },
      }))
      .addCase(listPhotoAlbumAction.rejected, (state) => ({
        ...state,
        loadingList: false,
      }))
      .addCase(createPhotoAlbumAction.fulfilled, (state) => ({
        ...state,
        triggerSavePhotoAlbum: { id: uuidv4() },
      }))
      .addCase(updatePhotoAlbumAction.fulfilled, (state) => ({
        ...state,
        triggerSavePhotoAlbum: { id: uuidv4() },
      }))

      .addCase(deletePhotoAlbumAction.pending, (state) => ({
        ...state,
        loadingList: true,
      }))
      .addCase(deletePhotoAlbumAction.fulfilled, (state) => ({
        ...state,
        triggerSavePhotoAlbum: { id: uuidv4() },
      }))
      .addCase(deletePhotoAlbumAction.rejected, (state) => ({
        ...state,
        loadingList: false,
      }))
      .addCase(activePhotoAlbumAction.fulfilled, (state) => ({
        ...state,
        triggerSavePhotoAlbum: { id: uuidv4() },
        loadingList: false,
      }))
      .addCase(inactivePhotoAlbumAction.fulfilled, (state) => ({
        ...state,
        triggerSavePhotoAlbum: { id: uuidv4() },
        loadingList: false,
      }))
      .addCase(getResourcePhotoAlbumAction.pending, (state) => ({
        ...state,
        loadingDetail: true,
      }))
      .addCase(getResourcePhotoAlbumAction.fulfilled, (state, action) => {
        const resourceType = action.meta.arg.payload.type;
        const resourceKey = getStateKeyByType(resourceType);
        return {
          ...state,
          loadingDetail: false,
          photoAlbumDetail: {
            ...state.photoAlbumDetail,
            [resourceKey]: action.payload?.data || [],
          },
        } as any;
      })
      .addCase(getResourcePhotoAlbumAction.rejected, (state) => ({
        ...state,
        loadingDetail: false,
      }))
      .addCase(downloadResourcePhotoAlbumAction.rejected, (state, action) => {
        const resourceType = action.meta.arg.typeImage;
        const resourceKey = getStateKeyByType(resourceType);
        return {
          ...state,
          loadingDownload: {
            ...state.loadingDownload,
            [resourceKey]: false,
          },
        } as any;
      })
      .addCase(downloadResourcePhotoAlbumAction.fulfilled, (state, action) => {
        const resourceType = action.meta.arg.typeImage;
        const resourceKey = getStateKeyByType(resourceType);
        return {
          ...state,
          loadingDownload: {
            ...state.loadingDownload,
            [resourceKey]: false,
          },
        } as any;
      })
      .addCase(downloadResourcePhotoAlbumAction.pending, (state, action) => {
        const resourceType = action.meta.arg.typeImage;
        const resourceKey = getStateKeyByType(resourceType);
        return {
          ...state,
          loadingDownload: {
            ...state.loadingDownload,
            [resourceKey]: true,
          },
        } as any;
      });
  },
});

export const { setPhotoAlbumStateAction, resetPhotoAlbumStateAction } =
  photoAlbumSlice.actions;

export default photoAlbumSlice.reducer;
